import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Store,
  User,
  MapPin,
  Briefcase,
  ShieldCheck,
  Clock,
  Loader2,
  Tag,
} from "lucide-react";
import type { Vendor } from "../../services/api";
import { formatVendorArray } from "./vendorUtils";

function formatDisplayValue(value?: string | number | boolean | null): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value?: string | number | boolean | null;
}) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="min-w-[148px] shrink-0 text-sm text-slate-500">
        {label}:
      </span>
      <span className="break-words text-sm font-medium text-zinc-900">
        {formatDisplayValue(value)}
      </span>
    </div>
  );
}

function VendorDetailSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2.5 text-base font-bold text-zinc-900">
        {/* <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC] text-sm font-bold text-[#8B3232]">
          {number}
        </span> */}
        <span className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FCECEC] [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-[#8B3232]">
            {icon}
          </span>
          {title}
        </span>
      </h3>
      <div className="rounded-xl border border-zinc-200/90 bg-[#F4F7F9] p-4 shadow-sm">
        {children}
      </div>
    </section>
  );
}

export interface VendorDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: Vendor | null;
  loading?: boolean;
  /** Super admin sees compliance, admin notes, past projects JSON */
  showAdminFields?: boolean;
}

export function VendorDetailsModal({
  open,
  onOpenChange,
  vendor,
  loading = false,
  showAdminFields = false,
}: VendorDetailsModalProps) {
  const logoUrl = vendor?.logo ?? vendor?.logo_url;
  const verificationStatus = vendor?.verification_status?.trim() ?? "Pending";

  const pastProjectsDisplay = (() => {
    if (!vendor?.past_projects) {
      return "—";
    }
    if (Array.isArray(vendor.past_projects)) {
      return JSON.stringify(vendor.past_projects, null, 2);
    }
    return String(vendor.past_projects);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
        <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                aria-hidden
              >
                <Store className="h-5 w-5 text-[#8B3232]" strokeWidth={2.25} />
              </span>
              Vendor Details
            </DialogTitle>
            {vendor && !loading && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#f0dede] bg-[#FDF7F7] px-3.5 py-1 text-sm font-semibold capitalize text-[#8B3232]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-[#c53030]"
                  aria-hidden
                />
                {verificationStatus}
              </span>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4 bg-white">
            <Loader2 className="h-12 w-12 animate-spin text-[#8B3232]" />
            <p className="animate-pulse font-medium text-muted-foreground">
              Fetching vendor details...
            </p>
          </div>
        ) : vendor ? (
          <>
            <div className="max-h-[min(80vh,730px)] overflow-y-auto bg-white [scrollbar-color:#8B3232_#f4f4f5] [scrollbar-width:thin]">
              <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                  <div className="min-w-0 space-y-1 sm:flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                      Vendor name
                    </p>
                    <p className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
                      {vendor.vendor_name || "—"}
                    </p>
                    {vendor.company_name && (
                      <p className="text-sm font-medium text-zinc-500">
                        {vendor.company_name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Vendor type
                      </p>
                      <p className="text-sm font-semibold capitalize text-zinc-900">
                        {vendor.vendor_type ?? "—"}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Empanelment
                      </p>
                      <p className="text-sm font-semibold capitalize text-zinc-900">
                        {vendor.empanelment_status ?? "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6 px-6 py-5">
                <VendorDetailSection title="Basic Details" icon={<Tag />}>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <DetailRow
                        label="Vendor Name"
                        value={vendor.vendor_name}
                      />
                      <DetailRow
                        label="Category"
                        value={formatVendorArray(
                          vendor.vendor_category_names ??
                            vendor.vendor_category ??
                            vendor.category
                        )}
                      />
                      <DetailRow
                        label="Sub-category"
                        value={formatVendorArray(
                          vendor.vendor_sub_category_names ??
                            vendor.vendor_sub_category
                        )}
                      />
                      <DetailRow
                        label="Company Name"
                        value={vendor.company_name}
                      />
                      <DetailRow
                        label="Vendor Type"
                        value={vendor.vendor_type}
                      />
                      <DetailRow
                        label="Short Description"
                        value={vendor.short_description}
                      />
                    </div>
                    <div className="flex shrink-0 flex-col items-center sm:pl-2">
                      <span className="mb-1.5 text-xs font-medium text-slate-500">
                        Logo
                      </span>
                      <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-white">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt="Vendor logo"
                            className="h-full w-full object-contain p-1"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No logo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </VendorDetailSection>

                <VendorDetailSection
                  title="Contact Information"
                  icon={<User />}
                >
                  <div className="space-y-0.5">
                    <DetailRow
                      label="Contact Person"
                      value={vendor.contact_person_name}
                    />
                    <DetailRow label="Mobile" value={vendor.mobile_number} />
                    <DetailRow label="Email" value={vendor.email} />
                    <DetailRow
                      label="Office Address"
                      value={vendor.office_address}
                    />
                    <DetailRow label="City" value={vendor.city} />
                    <DetailRow label="State" value={vendor.state} />
                    <DetailRow label="PIN Code" value={vendor.pin_code} />
                  </div>
                </VendorDetailSection>

                <VendorDetailSection title="Service Coverage" icon={<MapPin />}>
                  <div className="space-y-0.5">
                    <DetailRow
                      label="Service Areas"
                      value={formatVendorArray(vendor.service_areas)}
                    />
                    <DetailRow
                      label="PAN India Service"
                      value={vendor.pan_india_service}
                    />
                  </div>
                </VendorDetailSection>

                <VendorDetailSection
                  title="Services & Experience"
                  icon={<Briefcase />}
                >
                  <div className="space-y-0.5">
                    <DetailRow
                      label="Services Offered"
                      value={formatVendorArray(vendor.services_offered)}
                    />
                    <DetailRow
                      label="Specialization"
                      value={vendor.specialization}
                    />
                    <DetailRow
                      label="Years of Experience"
                      value={vendor.years_of_experience}
                    />
                    <DetailRow
                      label="Hostel Projects Experience"
                      value={vendor.hostel_projects_experience}
                    />
                    {showAdminFields && (
                      <DetailRow
                        label="Past Projects"
                        value={pastProjectsDisplay}
                      />
                    )}
                  </div>
                </VendorDetailSection>

                <VendorDetailSection
                  title="Verification"
                  icon={<ShieldCheck />}
                >
                  <div className="space-y-0.5">
                    <DetailRow
                      label="Empanelment Status"
                      value={vendor.empanelment_status}
                    />
                    <DetailRow
                      label="Verification Status"
                      value={vendor.verification_status}
                    />
                    {showAdminFields && (
                      <>
                        <DetailRow
                          label="Compliance Confirmation"
                          value={vendor.compliance_confirmation}
                        />
                        <DetailRow
                          label="Admin Notes"
                          value={vendor.admin_notes}
                        />
                      </>
                    )}
                  </div>
                </VendorDetailSection>

                <VendorDetailSection title="Availability" icon={<Clock />}>
                  <div className="space-y-0.5">
                    <DetailRow
                      label="Available for Work"
                      value={vendor.available_for_work}
                    />
                    <DetailRow
                      label="Working Days"
                      value={formatVendorArray(vendor.working_days)}
                    />
                    <DetailRow
                      label="Service Timings"
                      value={vendor.service_timings}
                    />
                  </div>
                </VendorDetailSection>
              </div>
            </div>

            <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="bg-white px-6 py-10 text-center text-muted-foreground">
            No vendor data to display.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
